import { Component, OnInit, AfterViewInit, ViewChild, OnDestroy, Input, HostListener } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { IOrganisationsHierarchy, OrganisationDataService, OrganisationsHierarchy } from '@services/organisation-data.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { distinctUntilChanged, filter, finalize, takeUntil } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { LevelAsset, AssetsDataService } from '@services/assets-data.service';
// import { RowSelectEventArgs, SelectionSettingsModel } from '@syncfusion/ej2-angular-grids';
import { IAccount } from '@services/accounts.service';
import { WatchListComponent } from '@components/watch-list/watch-list.component';
import { DatapointsDataService } from '@services/datapoints-data.service';
import { MatFormField } from '@angular/material/form-field';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';

/**
 * Default Class
 */
@Component({
	selector: 'app-asset-list',
	templateUrl: './asset-list.component.html',
	styleUrls: ['./asset-list.component.scss'],
	animations: [
		trigger('detailExpand', [
			state('collapsed', style({ height: '0px', minHeight: '0' })),
			state('expanded', style({ height: '*' })),
			transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
		]),
	]
})
export class AssetListComponent implements OnInit, OnDestroy, AfterViewInit {

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.accountID, imgTypes.organisationassets).toLowerCase()
	}

	/** Page Settings */
	public pageSettings?: Object;

	/** Grid Selection Options */
	// public selectionOptions?: SelectionSettingsModel;

	/** paginator */
	@ViewChild(MatPaginator) paginator!: MatPaginator;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Watch Status */
	public watchStatus: boolean = false
	/** Selected Nodes */
	public selectedNodes: string = ""
	/** Selected Node name */
	public selectedNodeName: string = ""
	/** Assets List */
	public assets: LevelAsset[] = [];
	/** watchedAssets List */
	public watchedAssets: LevelAsset[] = [];

	public filteredWatchedAssets: LevelAsset[] = [];
	/** Selected Asset */
	public selectedAsset: LevelAsset
	/** Datasource */
	public dataSource: MatTableDataSource<LevelAsset>;

	// public displayedColumns = ['assetID', 'name', 'description']
	/** Table Columns to display */
	public columnsToDisplay = ['Name', 'Description']; //, 'Watch']

	/** Extended columns to display */
	columnsToDisplayWithExpand = [...this.columnsToDisplay, 'Expanded'];
	/** Expanded Element */
	public expandedElement!: LevelAsset | null;

	/** accountID */
	@Input() accountID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	@Input() public OrganisationUID: string;

	@ViewChild(RouterOutlet) routerOutlet: RouterOutlet;

	public fields: Object = { text: 'Name', value: 'AssetID' }

	isMobileView: boolean = false;

	/** paginator */
	@ViewChild("filterBar") filterBar!: MatFormField;

	public hierarchy: OrganisationsHierarchy[];

	/** MainLocations */
	public MainLocations: OrganisationsHierarchy[] = [];

	/** locationFields */
	public locationFields: { text: string, value: string } = { text: "Name", value: "Id" };

	/** selectedLocation */
	public selectedLocation?: OrganisationsHierarchy;

	/** locationDropdown */
	@ViewChild('dropdown') locationDropdown: DropDownList;

	/** assetList */
	public assetList: LevelAsset[] = [];

	/**
	 * Default Constructor
	 * @param route route
	 * @param router router
	 * @param OrganisationData OrganisationData
	 * @param AssetsDataService AssetsDataService
	 * @param siteSubscriptions siteSubscriptions
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private route: ActivatedRoute,
		private router: Router,
		public OrganisationData: OrganisationDataService,
		public AssetsDataService: AssetsDataService,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions,
		private datapointsDataService: DatapointsDataService
	) {
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.globalFunctions.displaySpinner(true);
		this.pageSettings = { pageSizes: true, pageSize: 0 };
		// this.selectionOptions = { mode: 'Row', type: 'Single' };

		if (this.route.parent) {
			combineLatest([
				this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.route.queryParams.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged())
			]).pipe(
				// Ensure that subscriptions are not null or undefined
				filter(([queryParams, paramMap, hierarchicalData]) =>
					!!queryParams && !!paramMap && !!hierarchicalData),
				finalize(() => {
					// this.globalFunctions.displaySpinner(false);
				})
			).subscribe({
				next: ([selectedAccountGUID, selectedAccount, selectedOrganisationUID, queryParams, paramMap, hierarchicalData]) => {
					this.watchStatus = queryParams['watch'] || false;
					// console.log(paramMap);
					const paramAsset = paramMap.get('organisation');
					this.accountID = selectedAccountGUID;
					this.OrganisationUID = selectedOrganisationUID;

					const currentURL = this.router.url;
					let assetID: any = currentURL.split('/').pop()
					if (assetID) {
						assetID = parseInt(assetID)
					}

					if (paramAsset) {
						this.selectedNodes = paramAsset;
						try {
							/** Clear the Asset Items */
							this.selectedAsset = new LevelAsset();
							if (hierarchicalData != '') {
								let orgData = <IOrganisationsHierarchy[]>JSON.parse(hierarchicalData);
								this.hierarchy = JSON.parse(hierarchicalData);
								this.assetList = this.flattenAssets(this.hierarchy);
								this.MainLocations = [];

								if (this.hierarchy[0].SubChild) {
									this.hierarchy[0].SubChild.forEach(subchild => {
										this.MainLocations.push(subchild);
									});
								}
								let Found = <OrganisationsHierarchy>this.OrganisationData.findObjectByUID(paramAsset, orgData);
								if (Found) {
									// console.log(Found)
									this.selectedNodeName = Found.Description;
									// const result: any = this.OrganisationData.findSubChildNamesById(Found, paramAsset)
									this.assets = <LevelAsset[]>this.AssetsDataService.findAssets(Found, this.watchStatus);// <AssetItem[]>result
									// this.watchedAssets = this.assets.filter((x: LevelAsset) => x.Watch == true)
									this.getWatchList();
									this.dataSource = new MatTableDataSource(this.assets);
									if (assetID) {
										let a = this.assets.find((x: LevelAsset) => x.AssetID == assetID);
										if (a) {
											this.selectedAsset = a;
										}
									}
								} else {
									this.selectedNodeName = "";
									this.assets = [];
								}
							}
						}
						catch (error) { console.error(error) };
					}
					else {
						this.selectedNodes = "";
						this.selectedNodeName = "";
					}
				},
				error: (error) => {
					this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				}
			});

			this.siteSubscriptions.refreshWatchList$.pipe()
				.subscribe({
					next: (refresh) => {
						if (refresh) {
							this.getWatchList()
							this.siteSubscriptions.RefreshWatchList(false);
						}
					}
				})
		}
	}

	/** get watched assets from the WatchListAssets table */
	getWatchList() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.AssetsDataService.getWatchListAssets$(this.accountID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (watchList: any) => {
				// let list: LevelAsset[] = this.flattenAssets(this.hierarchy);
				let listAssetIDs: number[] = [];
				this.assetList.forEach(asset => {
					listAssetIDs.push(asset.AssetID);
				})
				this.watchedAssets = watchList.filter((x: LevelAsset) => listAssetIDs.includes(x.AssetID));
				this.filteredWatchedAssets = this.watchedAssets
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "AssetList Error: Retrieving Watched Assets", false)
				}
			},
		});
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/** onFiltering */
	onFiltering(event: any) {
		this.filterAssets();
		event.cancel = true;
		const query = (event.target as HTMLInputElement).value.toLowerCase();
		// const query = event.text.toLowerCase();
		this.filteredWatchedAssets = this.filteredWatchedAssets.filter(data => {
			const name = data.Name.toLowerCase();
			const description = data.Description.toLowerCase();

			// Combine the necessary fields for filtering
			const combinedStr = `${name} ${description}`;
			return combinedStr.includes(query);
		});
	}

	// this.route.queryParams.subscribe(queryParams => {

	// 	this.watchStatus = queryParams['watch'] || false;

	// 	this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
	// 		.subscribe({
	// 			next: (paramMap) => {
	// 				console.log(paramMap);
	// 			}
	// 		});

	// 	this.route.parent?.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()
	// 	).subscribe({
	// 		next: (parentParamMap) => {
	// 			const paramAsset = parentParamMap.get('sublocation');
	// 			if (paramAsset) {
	// 				this.selectedNodes = paramAsset;
	// 				let Found: OrganisationsHierarchy;

	// 				try {
	// 					/** Clear the Asset Items */
	// 					this.selectedAsset = new LevelAsset();

	// 					this.siteSubscriptions.hierarchicalData$.pipe(
	// 						takeUntil(this.finalise),
	// 						distinctUntilChanged()
	// 					).subscribe({
	// 						next: (data: string) => {
	// 							let orgData = <IOrganisationsHierarchy[]>JSON.parse(data);
	// 							Found = this.OrganisationData.findObjectByUID(paramAsset, orgData);
	// 							if (Found) {
	// 								// console.log(Found)
	// 								this.selectedNodeName = Found.Description;
	// 								// const result: any = this.OrganisationData.findSubChildNamesById(Found, paramAsset)
	// 								this.assets = <LevelAsset[]>this.AssetsDataService.findAssets(Found, this.watchStatus);// <AssetItem[]>result
	// 								this.dataSource = new MatTableDataSource(this.assets);
	// 								this.selectedAsset = this.assets[0];
	// 							} else {
	// 								this.selectedNodeName = "";
	// 								this.assets = [];
	// 							}
	// 						},
	// 						error: (error) => { this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.hierarchicalData$", false); },
	// 						//complete: () => { console.log("this.siteSubscriptions.hierarchicalData$.subscribe: Complete"); }
	// 					});
	// 				}
	// 				catch (error) { console.log(error) };
	// 			}
	// 			else {
	// 				this.selectedNodes = "";
	// 				this.selectedNodeName = "";
	// 			}
	// 		},
	// 		error: (error) => { this.globalFunctions.HandleError(error, "Error: this.route.parent?.paramMap", false); },
	// 		//complete: () => { console.log("this.route.parent?.paramMap.subscribe: Complete"); }
	// 	});
	// });

	// this.route.queryParams.subscribe(params => {
	//     this.queryStatus = params['status']
	// })




	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Page after view init Event Handler
	 */
	ngAfterViewInit() {
		//this.paginator.pageSize = 25
		if (this.dataSource) {
			this.dataSource.paginator = this.paginator;
		}
	}

	/**
	 * Asset Clicked Event
	 * @param asset
	 */
	assetClick(asset: any) {
		this.selectedAsset = asset;
		// console.log(asset)
	}

	// /**
	//  * Asset Clicked Event
	//  * @param args
	//  */
	// rowSelected(args: RowSelectEventArgs): void {
	// 	// return
	// 	this.selectedAsset = args.data as LevelAsset;
	// 	this.showAsset(this.selectedAsset.AssetID);
	// }

	/** assetSelected */
	assetSelected(asset: LevelAsset) {
		if (asset.AssetID != this.selectedAsset.AssetID) {
			this.selectedAsset = asset;
			this.showAsset(this.selectedAsset.AssetID);
		}
	}

	/**
	 * Selected Row
	 * @param args
	 */
	trRowSelected(args: LevelAsset) {
		return
		this.selectedAsset = args;
		this.showAsset(this.selectedAsset.AssetID);
	}

	/**
	 *
	 * @param assetID
	 */
	private showAsset(assetID: number) {
		try {
			// Create the new child route segment
			// let currentUrl = this.router.url.split("/");
			// // let newChildSegment = currentUrl.slice(0, 6).join("/");
			// let newChildSegment2 = currentUrl.join("/");
			// if (currentUrl.length > 4) {
			// 	newChildSegment2 = currentUrl.slice(0, -1).join("/");
			// }

			this.siteSubscriptions.SetSelectedAsset(this.selectedAsset)

			this.router.navigate([assetID], { relativeTo: this.route });

			// Navigate to the new URL with the changed child route
			// this.router.navigate([`${newChildSegment2}/${assetID}`], { relativeTo: this.route.parent });

		} catch (error) { console.error(error); }
	}

	/**
	 * Edit the Location details
	 * @param imageURL
	 */
	viewImage(imageURL: string): void {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * filterByLocation
	 * @param event event 
	 * @param cancel cancel 
	 */
	filterByLocation(event: any, cancel?: boolean) {
		if (cancel) {
			this.selectedLocation = undefined;
			this.locationDropdown.clear();
		} else if (event.itemData) {
			this.selectedLocation = <OrganisationsHierarchy>event.itemData;
		}
		this.filterAssets();
	}

	/**
	 * filterAssets
	 */
	filterAssets() {
		if (this.selectedLocation) {
			let list = this.flattenAssets([this.selectedLocation]);
			this.filteredWatchedAssets = this.watchedAssets.filter((x: LevelAsset) => list.map((y: LevelAsset) => y.AssetID).includes(x.AssetID));
		} else {
			this.filteredWatchedAssets = this.watchedAssets;
		}
	}
}
